import { FAMILIES } from "../domain/config.js";
import { START_RATINGS } from "./templates.js";
import type { Profile } from "./types.js";
export interface StorageDriver {
    readonly persistent?: boolean;
    get(key: string): Promise<unknown>;
    set(key: string, value: unknown): Promise<void>;
    remove(key: string): Promise<void>;
}
export function createProfile(id: string, name: string, at: number): Profile {
    return { version: 1, id, name: name.trim().slice(0, 24) || "Captain", skills: Object.fromEntries(FAMILIES.map(f => [f, { rating: START_RATINGS[f], attempts: 0, correct: 0 }])) as Profile["skills"], unlocked: ["add"], attempts: [], facts: {}, patterns: {}, createdAt: at, totalAttempts: 0 };
}
function validateProfile(value: unknown): value is Profile {
    if (!value || typeof value !== "object")
        return false;
    const p = value as Profile;
    const count = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
    if (!count(p.totalAttempts) || !Number.isFinite(p.createdAt) || p.createdAt < 0 || !p.facts || Array.isArray(p.facts) || !p.patterns || Array.isArray(p.patterns))
        return false;
    const facts = Object.values(p.facts), patterns = Object.values(p.patterns);
    if (facts.length > 2000 || patterns.length > 40 || facts.some(v => !v || typeof v !== "object" || !count(v.attempts) || !count(v.missed) || v.missed > v.attempts) || patterns.some(v => !count(v)))
        return false;
    if (Array.isArray(p.attempts) && p.attempts.some(a => !a || typeof a !== "object" || !FAMILIES.includes(a.family) || typeof a.answer !== "string" || typeof a.correct !== "boolean" || !Number.isFinite(a.ratingBefore) || !Number.isFinite(a.ratingAfter)))
        return false;
    return p.version === 1 && typeof p.id === "string" && p.id.length <= 100 && typeof p.name === "string" && p.name.length <= 24 && !!p.skills && FAMILIES.every(f => p.skills[f] && Number.isFinite(p.skills[f].rating) && p.skills[f].rating >= 400 && p.skills[f].rating <= 2200 && Number.isSafeInteger(p.skills[f].attempts) && p.skills[f].attempts >= 0 && Number.isSafeInteger(p.skills[f].correct) && p.skills[f].correct >= 0 && p.skills[f].correct <= p.skills[f].attempts) && Array.isArray(p.unlocked) && p.unlocked.includes("add") && p.unlocked.every(f => FAMILIES.includes(f)) && Array.isArray(p.attempts) && p.attempts.length <= 1000 && typeof p.facts === "object" && p.facts !== null && typeof p.patterns === "object" && p.patterns !== null;
}
export class ProfileStore {
    profiles: Profile[] = [];
    activeId = "";
    constructor(private driver: StorageDriver) { }
    async load(): Promise<void> {
        const raw = await this.driver.get("sky-harbor:profiles:v1");
        if (raw && typeof raw === "object") {
            const data = raw as {
                profiles?: unknown[];
                activeId?: string;
            };
            if (Array.isArray(data.profiles))
                this.profiles = data.profiles.filter(validateProfile).slice(0, 8);
            if (typeof data.activeId === "string")
                this.activeId = data.activeId;
        }
        if (!this.profiles.length)
            this.profiles = [createProfile("captain-1", "Captain", Date.now())];
        if (!this.profiles.some(p => p.id === this.activeId))
            this.activeId = this.profiles[0].id;
    }
    get active(): Profile { return this.profiles.find(p => p.id === this.activeId) || this.profiles[0]; }
    async save(): Promise<void> { await this.driver.set("sky-harbor:profiles:v1", { activeId: this.activeId, profiles: this.profiles }); }
    async add(name: string): Promise<Profile> {
        if (this.profiles.length >= 8)
            throw new Error("This device already has eight profiles.");
        const p = createProfile((crypto.randomUUID?.() || "profile-" + Array.from(crypto.getRandomValues(new Uint32Array(3))).map(n => n.toString(16)).join("")), name, Date.now());
        this.profiles.push(p);
        this.activeId = p.id;
        await this.save();
        return p;
    }
    async remove(id: string): Promise<void> {
        this.profiles = this.profiles.filter(p => p.id !== id);
        if (!this.profiles.length)
            this.profiles = [createProfile((crypto.randomUUID?.() || "profile-" + Array.from(crypto.getRandomValues(new Uint32Array(3))).map(n => n.toString(16)).join("")), "Captain", Date.now())];
        if (this.activeId === id)
            this.activeId = this.profiles[0].id;
        await this.save();
    }
}
